import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, IconButton, Tab, TabList, Text } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export type HomeTabsCarouselItem = {
  label: string;
  subtitle: string;
  icon: React.ReactNode;
  iconAccent: string;
  selectedBgGradient: string;
  selectedBorderAccent: string;
};

type HomeTabsCarouselProps = {
  tabs: HomeTabsCarouselItem[];
};

const SCROLL_TOLERANCE = 4;
const cardsBorderRadii = "8px";

const HomeTabsCarousel: React.FC<HomeTabsCarouselProps> = ({ tabs }) => {
  const tabsScrollRef = useRef<HTMLDivElement>(null);

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";

  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const softShadow = theme.shadowSm;

  const updateArrowVisibility = () => {
    const container = tabsScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > SCROLL_TOLERANCE;

    const canScrollLeft = scrollLeft > SCROLL_TOLERANCE;
    const canScrollRight = scrollLeft < maxScrollLeft - SCROLL_TOLERANCE;

    setShowLeftArrow(hasOverflow && canScrollLeft);
    setShowRightArrow(hasOverflow && canScrollRight);
  };

  useEffect(() => {
    const container = tabsScrollRef.current;
    if (!container) return;

    updateArrowVisibility();

    const handleScroll = () => updateArrowVisibility();
    const handleResize = () => updateArrowVisibility();

    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    const resizeObserver = new ResizeObserver(() => {
      updateArrowVisibility();
    });

    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [tabs.length]);

  useEffect(() => {
    if (hasAutoScrolled || !tabsScrollRef.current) return;

    const container = tabsScrollRef.current;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const hasOverflow = maxScrollLeft > SCROLL_TOLERANCE;

    if (!hasOverflow) {
      updateArrowVisibility();
      setHasAutoScrolled(true);
      return;
    }

    const scrollAmount = container.clientWidth;
    const totalScrolls = Math.ceil(container.scrollWidth / scrollAmount);
    let currentStep = 0;

    const interval = window.setInterval(() => {
      const latestMaxScrollLeft = container.scrollWidth - container.clientWidth;
      const canScrollRight =
        container.scrollLeft < latestMaxScrollLeft - SCROLL_TOLERANCE;

      if (canScrollRight && currentStep < totalScrolls) {
        container.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        });

        currentStep += 1;

        window.setTimeout(updateArrowVisibility, 350);
        return;
      }

      container.scrollTo({
        left: 0,
        behavior: "smooth",
      });

      window.clearInterval(interval);
      setHasAutoScrolled(true);

      window.setTimeout(() => {
        if (tabsScrollRef.current) {
          tabsScrollRef.current.scrollLeft = 0;
        }

        updateArrowVisibility();
      }, 450);
    }, 2500);

    return () => window.clearInterval(interval);
  }, [hasAutoScrolled, tabs.length]);

  const scrollTabs = (direction: "left" | "right") => {
    const container = tabsScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    const maxScrollLeft = scrollWidth - clientWidth;

    const canScrollLeft = scrollLeft > SCROLL_TOLERANCE;
    const canScrollRight = scrollLeft < maxScrollLeft - SCROLL_TOLERANCE;

    if (direction === "left" && !canScrollLeft) {
      updateArrowVisibility();
      return;
    }

    if (direction === "right" && !canScrollRight) {
      updateArrowVisibility();
      return;
    }

    container.scrollBy({
      left: direction === "left" ? -clientWidth : clientWidth,
      behavior: "smooth",
    });

    window.setTimeout(() => {
      if (tabsScrollRef.current) {
        const latestMaxScrollLeft =
          tabsScrollRef.current.scrollWidth - tabsScrollRef.current.clientWidth;

        if (tabsScrollRef.current.scrollLeft <= SCROLL_TOLERANCE) {
          tabsScrollRef.current.scrollLeft = 0;
        }

        if (
          tabsScrollRef.current.scrollLeft >=
          latestMaxScrollLeft - SCROLL_TOLERANCE
        ) {
          tabsScrollRef.current.scrollLeft = latestMaxScrollLeft;
        }
      }

      updateArrowVisibility();
    }, 350);
  };

  const baseTabStyle = {
    color: textColor,
    border: `1px solid ${theme.border}`,
    borderRadius: cardsBorderRadii,
    p: { base: 2, md: 3 },
    boxShadow: softShadow,
    bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.9)",
    _hover: {
      transform: "translateY(-2px)",
      boxShadow: theme.shadowSm,
      bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
    },
    transition: "all 0.2s ease",
  };

  const selectedTabStyle = (bgGradient: string, borderAccent: string) => ({
    bg: bgGradient,
    color: textColor,
    transform: "translateY(-2px)",
    boxShadow: theme.shadowMd,
    borderColor: borderAccent,
    borderWidth: "1.5px",
  });

  const iconChip = (accent: string) => ({
    p: "9px",
    borderRadius: "5px",
    bg: isDark ? `${accent}20` : `${accent}1A`,
    boxShadow: isDark
      ? "inset 0 0 0 1px rgba(255,255,255,0.04)"
      : "inset 0 0 0 1px rgba(42,44,59,0.04)",
    mb: 1,
  });

  const tabFlex = {
    base: "0 0 calc((100% - 16px) / 3)",
    md: "1 1 calc(20% - 1rem)",
  };

  return (
    <Box position="relative" w="100%" flexShrink={0} px={4}>
      <IconButton
        aria-label="Scroll tabs left"
        icon={<ChevronLeftIcon boxSize={5} />}
        position="absolute"
        left={2}
        top="50%"
        transform="translateY(-50%)"
        zIndex={3}
        size="xs"
        minW="28px"
        h="28px"
        borderRadius="5px"
        bg="rgba(24, 28, 42, 0.94)"
        color="white"
        display={{
          base: showLeftArrow ? "flex" : "none",
          md: "none",
        }}
        onClick={() => scrollTabs("left")}
        _hover={{
          bg: "rgba(0,0,0,0.78)",
        }}
        _active={{
          bg: "rgba(0,0,0,0.85)",
        }}
      />

      <IconButton
        aria-label="Scroll tabs right"
        icon={<ChevronRightIcon boxSize={5} />}
        position="absolute"
        right={2}
        top="50%"
        transform="translateY(-50%)"
        zIndex={3}
        size="xs"
        minW="28px"
        h="28px"
        borderRadius="5px"
        bg="rgba(24, 28, 42, 0.94)"
        color="white"
        display={{
          base: showRightArrow ? "flex" : "none",
          md: "none",
        }}
        onClick={() => scrollTabs("right")}
        _hover={{
          bg: "rgba(0,0,0,0.78)",
        }}
        _active={{
          bg: "rgba(0,0,0,0.85)",
        }}
      />

      <TabList
        ref={tabsScrollRef}
        gap={{ base: 2, md: 3 }}
        flexWrap={{ base: "nowrap", md: "wrap" }}
        overflowX={{ base: "auto", md: "visible" }}
        overflowY="hidden"
        scrollSnapType={{ base: "x mandatory", md: "none" }}
        p={{ base: 1, md: 2 }}
        borderRadius={cardsBorderRadii}
        bg={isDark ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.45)"}
        flexShrink={0}
        mb={2}
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.label}
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="700"
            maxW={{ base: "none", md: "220px" }}
            minW={{ base: "0", md: "auto" }}
            flex={tabFlex}
            scrollSnapAlign="start"
            overflow="hidden"
            {...baseTabStyle}
            _selected={selectedTabStyle(
              tab.selectedBgGradient,
              tab.selectedBorderAccent,
            )}
          >
            <Flex
              direction="column"
              align="center"
              justify="center"
              borderRadius={cardsBorderRadii}
              p={{ base: 1, md: 2 }}
              gap={1}
              w="100%"
              minW={0}
              overflow="hidden"
            >
              <Box {...iconChip(tab.iconAccent)}>{tab.icon}</Box>

              <Box
                maxW="100%"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {tab.label}
              </Box>

              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="500"
                noOfLines={1}
                maxW="100%"
              >
                {tab.subtitle}
              </Text>
            </Flex>
          </Tab>
        ))}
      </TabList>
    </Box>
  );
};

export default HomeTabsCarousel;
